import { FormEvent, useEffect, useMemo, useState } from 'react';
import { api, extractError } from '../../../lib/api';
import { useToast } from '../../../components/admin/Toast';
import { dangerBtnText, inputClass, primaryBtn, secondaryBtn, slugify } from './shared';

export interface NamedSlugEntity {
  id: string;
  name: string;
  slug: string;
  sortOrder: number;
  isActive: boolean;
}

interface Draft {
  name: string;
  slug: string;
  sortOrder: number;
  isActive: boolean;
}

interface Props {
  endpoint: string;
  singular: string;
  plural: string;
  placeholder: string;
}

export default function NamedSlugTab({
  endpoint,
  singular,
  plural,
  placeholder,
}: Props): JSX.Element {
  const toast = useToast();
  const [items, setItems] = useState<NamedSlugEntity[]>([]);
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
  const [sortOrder, setSortOrder] = useState<number | null>(null);
  const [sortOrderManuallyEdited, setSortOrderManuallyEdited] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);

  const ensureUniqueSlug = (base: string): string => {
    if (!base) return '';
    const taken = new Set(items.map((i) => i.slug));
    if (!taken.has(base)) return base;
    let n = 2;
    while (taken.has(`${base}-${n}`)) n += 1;
    return `${base}-${n}`;
  };

  const nextSortOrder = useMemo(
    () => items.reduce((max, i) => Math.max(max, i.sortOrder), 0) + 1,
    [items]
  );

  const effectiveSortOrder = sortOrderManuallyEdited && sortOrder !== null ? sortOrder : nextSortOrder;

  const slugCollision = slug !== '' && items.some((i) => i.slug === slug);

  const load = async (): Promise<void> => {
    const response = await api.get<NamedSlugEntity[]>(endpoint);
    setItems(response.data);
  };

  useEffect(() => {
    void load();
    // endpoint is stable for the lifetime of the tab
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCreate = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    try {
      await api.post(endpoint, {
        name,
        slug,
        sortOrder: effectiveSortOrder,
        isActive: true,
      });
      setName('');
      setSlug('');
      setSlugManuallyEdited(false);
      setSortOrder(null);
      setSortOrderManuallyEdited(false);
      toast.success(`${singular[0]?.toUpperCase()}${singular.slice(1)} criado(a).`);
      await load();
    } catch (err) {
      toast.error(extractError(err, `Falha ao criar ${singular}`));
    }
  };

  const startEdit = (item: NamedSlugEntity): void => {
    setEditingId(item.id);
    setDraft({
      name: item.name,
      slug: item.slug,
      sortOrder: item.sortOrder,
      isActive: item.isActive,
    });
  };

  const saveEdit = async (): Promise<void> => {
    if (!editingId || !draft) return;
    try {
      await api.put(`${endpoint}/${editingId}`, draft);
      setEditingId(null);
      setDraft(null);
      toast.success(`${singular[0]?.toUpperCase()}${singular.slice(1)} atualizado(a).`);
      await load();
    } catch (err) {
      toast.error(extractError(err, `Falha ao atualizar ${singular}`));
    }
  };

  const remove = async (item: NamedSlugEntity): Promise<void> => {
    if (!window.confirm(`Apagar "${item.name}"?`)) return;
    try {
      await api.delete(`${endpoint}/${item.id}`);
      toast.success(`${singular[0]?.toUpperCase()}${singular.slice(1)} eliminado(a).`);
      await load();
    } catch (err) {
      toast.error(extractError(err, `Falha ao apagar ${singular}`));
    }
  };

  return (
    <section>
      <form
        onSubmit={(e) => {
          void handleCreate(e);
        }}
        className="mb-6 grid grid-cols-1 gap-2 md:grid-cols-[1fr_1fr_100px_auto]"
      >
        <input
          placeholder={placeholder}
          value={name}
          onChange={(e) => {
            const v = e.target.value;
            setName(v);
            if (!slugManuallyEdited) {
              setSlug(ensureUniqueSlug(slugify(v)));
            }
          }}
          required
          className={inputClass}
        />
        <input
          placeholder="slug"
          value={slug}
          onChange={(e) => {
            const v = e.target.value;
            setSlug(v);
            setSlugManuallyEdited(v !== '');
          }}
          required
          className={inputClass}
        />
        <input
          type="number"
          value={effectiveSortOrder}
          onChange={(e) => {
            setSortOrder(Number(e.target.value));
            setSortOrderManuallyEdited(true);
          }}
          className={inputClass}
          placeholder="Ordem"
        />
        <button type="submit" disabled={slugCollision} className={primaryBtn}>
          Criar
        </button>
      </form>
      {slugCollision ? (
        <p className="mb-4 text-sm text-admin-danger">
          Já existe um(a) {singular} com o slug "{slug}".
        </p>
      ) : null}

      <ul className="overflow-hidden rounded-lg border border-admin-border bg-admin-surface">
        {items.length === 0 ? (
          <li className="p-4 text-sm text-admin-muted">Sem {plural}.</li>
        ) : null}
        {items.map((item) => {
          const isEditing = editingId === item.id;
          return (
            <li
              key={item.id}
              className="flex flex-wrap items-center gap-3 border-b border-admin-border p-3 last:border-b-0"
            >
              {isEditing && draft ? (
                <>
                  <input
                    value={draft.name}
                    onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                    className={inputClass}
                  />
                  <input
                    value={draft.slug}
                    onChange={(e) => setDraft({ ...draft, slug: e.target.value })}
                    className={inputClass}
                  />
                  <input
                    type="number"
                    value={draft.sortOrder}
                    onChange={(e) => setDraft({ ...draft, sortOrder: Number(e.target.value) })}
                    className={`${inputClass} w-24`}
                  />
                  <label className="flex items-center gap-1 text-sm">
                    <input
                      type="checkbox"
                      checked={draft.isActive}
                      onChange={(e) => setDraft({ ...draft, isActive: e.target.checked })}
                    />
                    Ativo
                  </label>
                  <button type="button" onClick={() => void saveEdit()} className={primaryBtn}>
                    Guardar
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setEditingId(null);
                      setDraft(null);
                    }}
                    className={secondaryBtn}
                  >
                    Cancelar
                  </button>
                </>
              ) : (
                <>
                  <span className="flex-1">
                    {item.name}{' '}
                    <small className="text-admin-muted">({item.slug})</small>
                    {!item.isActive ? (
                      <span className="ml-2 rounded bg-admin-surface-alt px-2 py-0.5 text-xs text-admin-muted">
                        inativo
                      </span>
                    ) : null}
                  </span>
                  <span className="text-xs text-admin-muted">ordem {item.sortOrder}</span>
                  <button type="button" onClick={() => startEdit(item)} className={secondaryBtn}>
                    Editar
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      void remove(item);
                    }}
                    className={dangerBtnText}
                  >
                    Apagar
                  </button>
                </>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
